import { useState, useMemo, useEffect, useRef } from "react";
import * as Papa from "papaparse";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid
} from "recharts";
import { initializeApp } from "firebase/app";
import { getFirestore, doc, getDoc, setDoc } from "firebase/firestore";

// ── Firebase ───────────────────────────────────────────────────────────────
const firebaseConfig = {
  apiKey: "AIzaSyBmse67jWqYxZsHyU2S4CHrh6VBZ-mYkGs",
  authDomain: "gogow-flights.firebaseapp.com",
  projectId: "gogow-flights",
  storageBucket: "gogow-flights.firebasestorage.app",
  messagingSenderId: "392351733615",
  appId: "1:392351733615:web:198f1cbc6bdca728adfa9a",
};

const app         = initializeApp(firebaseConfig);
const db          = getFirestore(app);
const FLIGHTS_DOC = doc(db, "flightlog", "data");

async function dbLoad() {
  const snap = await getDoc(FLIGHTS_DOC);
  return snap.exists() ? (snap.data().flights ?? []) : [];
}

async function dbSave(flights) {
  await setDoc(FLIGHTS_DOC, { flights, updated_at: new Date().toISOString() });
}

// ── Themes ─────────────────────────────────────────────────────────────────
const THEMES = {
  dark: {
    bg:        "#050d1a",
    card:      "#0a1628",
    cardHover: "#0d1e35",
    border:    "#0e2240",
    text:      "#e0f4ff",
    textSoft:  "#c0dff5",
    muted:     "#4a7fa5",
    accent:    "#00d4ff",
    accentBg:  "rgba(0,212,255,0.12)",
    rowHover:  "rgba(0,212,255,0.03)",
    theadBg:   "rgba(0,212,255,0.05)",
    scrollbar: "#0e2240",
    inputBg:   "#020810",
    shadow:    "0 4px 24px rgba(0,0,0,0.4)",
  },
  light: {
    bg:        "#f0f5fb",
    card:      "#ffffff",
    cardHover: "#f8fafd",
    border:    "#d0dff0",
    text:      "#0d1e35",
    textSoft:  "#2a4a6b",
    muted:     "#6b8faf",
    accent:    "#0080b3",
    accentBg:  "rgba(0,128,179,0.10)",
    rowHover:  "rgba(0,128,179,0.04)",
    theadBg:   "rgba(0,128,179,0.06)",
    scrollbar: "#d0dff0",
    inputBg:   "#f0f5fb",
    shadow:    "0 4px 24px rgba(0,0,0,0.08)",
  }
};

const CHART_COLORS = ["#00aadd","#0077aa","#ff6b35","#e0a800","#2db87c","#a855f7","#ff9966","#3399cc","#ff6699","#006699"];
const ADMIN_PASSWORD = "flightlog2024";

// ── ID maps ────────────────────────────────────────────────────────────────
const SEAT_TYPE     = { "1":"Window","2":"Middle","3":"Aisle","0":"Unknown" };
const FLIGHT_CLASS  = { "1":"Economy","2":"Business","3":"First","0":"Unknown" };
const FLIGHT_REASON = { "1":"Leisure","2":"Business","0":"Other" };

// ── Aircraft families ──────────────────────────────────────────────────────
const AIRCRAFT_FAMILIES = [
  [/airbus a3[12][019].*neo/i,           "Airbus A320neo family"],
  [/airbus a321/i,                       "Airbus A321"],
  [/airbus a320/i,                       "Airbus A320"],
  [/airbus a319/i,                       "Airbus A319"],
  [/airbus a380/i,                       "Airbus A380"],
  [/airbus a350/i,                       "Airbus A350"],
  [/airbus a34[0-9]/i,                   "Airbus A340"],
  [/airbus a33[0-9]/i,                   "Airbus A330"],
  [/airbus a30[0-9]/i,                   "Airbus A300"],
  [/boeing 737\s*(max|\d00|[-–]\d)/i,    "Boeing 737"],
  [/boeing 737/i,                        "Boeing 737"],
  [/boeing 757/i,                        "Boeing 757"],
  [/boeing 787/i,                        "Boeing 787"],
  [/boeing 777/i,                        "Boeing 777"],
  [/boeing 767/i,                        "Boeing 767"],
  [/boeing 747/i,                        "Boeing 747"],
  [/embraer\s*(erj|e)[-\s]?1[49][05]/i, "Embraer E-Jet"],
  [/embraer\s*(erj|e)[-\s]?1[67][05]/i, "Embraer E-Jet"],
  [/embraer\s*(erj|e)[-\s]?1[89][05]/i, "Embraer E-Jet"],
  [/embraer/i,                           "Embraer"],
  [/mcdonnell douglas dc-8/i,            "McDonnell Douglas DC-8"],
  [/mcdonnell douglas dc-9/i,            "McDonnell Douglas DC-9"],
  [/mcdonnell douglas dc-10/i,           "McDonnell Douglas DC-10"],
  [/mcdonnell douglas md-/i,             "McDonnell Douglas MD"],
  [/bombardier (dash 8|q[0-9])/i,        "Bombardier Dash 8"],
  [/atr 7[02]/i,                         "ATR 72"],
  [/atr 4[02]/i,                         "ATR 42"],
  [/fokker 100/i,                        "Fokker 100"],
  [/british aerospace jetstream/i,       "BAe Jetstream"],
];

function normaliseAircraft(raw) {
  const name = (raw||"").trim().replace(/\s*\([A-Z0-9]{2,6}\)\s*$/, "").trim();
  for (const [re, family] of AIRCRAFT_FAMILIES) if (re.test(name)) return family;
  return name || "Unknown";
}

const extractIATA = str => { const m = String(str||"").match(/\(([A-Z]{3})\//); return m?m[1]:str; };
const extractCity = str => String(str||"").split("/")[0].trim();
const extractName = str => String(str||"").replace(/\s*\(.*?\)\s*$/,"").trim();

const AIRLINE_ALIASES = {
  "thomson airways":  "TUI",
  "tui airways":      "TUI",
  "british midland":  "bmi british midland",
  "bmi":              "bmi british midland",
  "easyjet":          "easyJet",
};

function normaliseAirline(raw) {
  const name = extractName(raw);
  return AIRLINE_ALIASES[name.toLowerCase()] ?? name;
}
const parseDuration = str => {
  const parts = String(str||"").trim().split(":").map(Number);
  return (parts.length>=2 && !isNaN(parts[0]) && !isNaN(parts[1])) ? parts[0]*60+parts[1] : 0;
};
const fmtDur = mins => {
  if (!mins) return "—";
  const totalHours = Math.floor(mins / 60);
  const days  = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  return `${days}d ${hours}h`;
};

function parseFlightDiaryCSV(file, cb) {
  const reader = new FileReader();
  reader.onload = e => {
    const lines = e.target.result.split(/\r?\n/);
    // Auto-detect where headers start — find the first line containing "Date" and "From"
    const headerIdx = lines.findIndex(l => l.includes("Date") && l.includes("From"));
    const cleaned = lines.slice(headerIdx >= 0 ? headerIdx : 2).join("\n");
    Papa.parse(cleaned, {
      header: true, skipEmptyLines: true,
      complete: r => {
        const flights = r.data.map(row => ({
          date:           (row["Date"] === "1999-01-01" ? "" : row["Date"])||"",
          flightNum:      row["Flight number"]||"",
          from:           extractIATA(row["From"]),
          fromCity:       extractCity(row["From"]),
          to:             extractIATA(row["To"]),
          toCity:         extractCity(row["To"]),
          depTime:        row["Dep time"]||"",
          arrTime:        row["Arr time"]||"",
          durationRaw:    row["Duration"]||"",
          durationMins:   parseDuration(row["Duration"]),
          airline:        normaliseAirline(row["Airline"]),
          aircraftRaw:    extractName(row["Aircraft"]),
          aircraftFamily: normaliseAircraft(row["Aircraft"]),
          registration:   (row["Registration"]||"").trim(),
          seat:           row["Seat number"]||"",
          seatType:       SEAT_TYPE[row["Seat type"]]  ||row["Seat type"]  ||"",
          flightClass:    FLIGHT_CLASS[row["Flight class"]]||row["Flight class"]||"",
          reason:         FLIGHT_REASON[row["Flight reason"]]||row["Flight reason"]||"",
          note:           row["Note"]||"",
        })).filter(f=>f.from && f.to);
        cb(flights);
      }
    });
  };
  reader.readAsText(file);
}

function top(arr, n=8) {
  const c={};
  arr.forEach(v=>v&&(c[v]=(c[v]||0)+1));
  return Object.entries(c).sort((a,b)=>b[1]-a[1]).slice(0,n).map(([name,value])=>({name,value}));
}

// ── CSS generator (takes a theme object) ──────────────────────────────────
function makeCSS(t) {
  return `
@import url('https://fonts.googleapis.com/css2?family=Archivo:wght@700;800;900&family=DM+Mono:wght@300;400;500&display=swap');
*{box-sizing:border-box;margin:0;padding:0;}
body{background:${t.bg};color:${t.text};font-family:'DM Mono',monospace;transition:background .25s,color .25s;}
::-webkit-scrollbar{width:4px;}
::-webkit-scrollbar-track{background:${t.bg};}
::-webkit-scrollbar-thumb{background:${t.scrollbar};border-radius:2px;}
.app{min-height:100vh;}
.hdr{padding:28px 40px 0;border-bottom:1px solid ${t.border};background:${t.bg};transition:background .25s,border-color .25s;position:sticky;top:0;z-index:100;}
.hdr h1{font-family:'Archivo',sans-serif;font-size:2.2rem;font-weight:800;letter-spacing:-1px;color:${t.text};}
.hdr h1 span{color:${t.accent};}
.hdr-top{display:flex;align-items:flex-start;justify-content:space-between;padding-bottom:0;}
.nav{display:flex;margin-top:20px;overflow-x:auto;-webkit-overflow-scrolling:touch;scrollbar-width:none;-ms-overflow-style:none;}.nav::-webkit-scrollbar{display:none;}
.nav button{padding:10px 22px;background:none;border:none;color:${t.muted};font-family:'DM Mono',monospace;font-size:0.72rem;letter-spacing:1.5px;text-transform:uppercase;cursor:pointer;border-bottom:2px solid transparent;transition:all .2s;white-space:nowrap;flex-shrink:0;}
.nav button.on{color:${t.accent};border-bottom:2px solid ${t.accent};}
.nav button:hover:not(.on){color:${t.text};}
.body{padding:32px 40px;background:${t.bg};min-height:calc(100vh - 110px);transition:background .25s;}
.sgrid{display:grid;grid-template-columns:repeat(auto-fill,minmax(165px,1fr));gap:14px;margin-bottom:28px;}
.sc{background:${t.card};border:1px solid ${t.border};border-radius:10px;padding:18px 20px;box-shadow:${t.shadow};transition:background .25s,border-color .25s;}
.sc .lb{color:${t.muted};font-size:0.62rem;letter-spacing:2px;text-transform:uppercase;margin-bottom:7px;}
.sc .vl{font-family:'Archivo',sans-serif;font-size:1.9rem;font-weight:800;color:${t.text};line-height:1;}
.sc .sb2{color:${t.muted};font-size:0.68rem;margin-top:5px;}
.g2{display:grid;grid-template-columns:1fr 1fr;gap:18px;margin-bottom:18px;}
.cc{background:${t.card};border:1px solid ${t.border};border-radius:10px;padding:22px;box-shadow:${t.shadow};transition:background .25s,border-color .25s;}
.cc h3{font-family:'Archivo',sans-serif;font-size:0.74rem;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:${t.muted};margin-bottom:18px;}
.cc.full{grid-column:1/-1;}
.sb{display:flex;flex-wrap:wrap;gap:10px;margin-bottom:16px;}
.sb input,.sb select{background:${t.inputBg};border:1px solid ${t.border};border-radius:6px;padding:9px 14px;color:${t.text};font-family:'DM Mono',monospace;font-size:0.75rem;outline:none;transition:border-color .2s,background .25s;}
.sb input{flex:1;min-width:180px;}
.sb select{min-width:130px;}
.sb input:focus,.sb select:focus{border-color:${t.accent};}
.sb select option{background:${t.card};color:${t.text};}
.rc{color:${t.muted};font-size:0.68rem;margin-bottom:10px;}
.tw{background:${t.card};border:1px solid ${t.border};border-radius:10px;overflow:auto;max-height:68vh;box-shadow:${t.shadow};transition:background .25s,border-color .25s;}
table{width:100%;border-collapse:collapse;font-size:0.73rem;}
thead{background:${t.theadBg};position:sticky;top:0;z-index:1;}
th{padding:11px 14px;text-align:left;color:${t.muted};font-weight:500;letter-spacing:.8px;text-transform:uppercase;font-size:0.62rem;white-space:nowrap;cursor:pointer;user-select:none;}
th:hover{color:${t.text};}
td{padding:10px 14px;border-top:1px solid ${t.border};color:${t.text};white-space:nowrap;}
tr:hover td{background:${t.rowHover};}
tr.clickable{cursor:pointer;}
.t{display:inline-block;padding:2px 8px;border-radius:4px;font-size:0.62rem;}
.tb{background:${t.accentBg};color:${t.accent};}
.to2{background:rgba(255,107,53,.12);color:#e05a20;}
.tg{background:rgba(0,160,80,.1);color:#00a050;}
.tp{background:rgba(150,80,220,.12);color:#9050dc;}
.ty{background:rgba(200,140,0,.1);color:#b07800;}
.loading{text-align:center;padding:80px;color:${t.muted};}
.spin{font-size:2rem;animation:spin 1.5s linear infinite;display:inline-block;margin-bottom:12px;}
@keyframes spin{from{transform:rotate(0deg);}to{transform:rotate(360deg)}}
@keyframes slideIn{from{transform:translateX(100%)}to{transform:translateX(0)}}
.empty{text-align:center;padding:60px;color:${t.muted};}
.empty h3{font-family:'Archivo',sans-serif;font-size:1.1rem;margin-bottom:6px;}
/* Theme toggle */
.theme-toggle{display:flex;align-items:center;gap:6px;background:${t.card};border:1px solid ${t.border};border-radius:20px;padding:4px;transition:background .25s,border-color .25s;flex-shrink:0;margin-top:4px;}
.theme-toggle button{background:none;border:none;padding:5px 10px;border-radius:16px;cursor:pointer;font-size:0.8rem;transition:all .2s;color:${t.muted};}
.theme-toggle button.active{background:${t.accent};color:${t.bg};}
.theme-toggle button:hover:not(.active){color:${t.text};}
@media(max-width:768px){.g2{grid-template-columns:1fr;}.body{padding:18px;}.hdr{padding:18px 18px 0;}.nav button{padding:9px 14px;font-size:0.66rem;}}
`;
}

// ── Tooltip ────────────────────────────────────────────────────────────────
function Tip({active, payload, label, theme}) {
  if (!active||!payload?.length) return null;
  const t = theme;
  return (
    <div style={{background:t.card,border:`1px solid ${t.border}`,borderRadius:6,padding:"9px 13px",fontSize:"0.72rem",boxShadow:t.shadow}}>
      <div style={{color:t.muted,marginBottom:3}}>{label}</div>
      {payload.map((p,i)=><div key={i} style={{color:p.color||t.accent}}>{p.value}</div>)}
    </div>
  );
}

// ── Bar list ───────────────────────────────────────────────────────────────
function BarList({data, theme}) {
  const t = theme;
  return data.map((d,i)=>(
    <div key={d.name} style={{display:"flex",alignItems:"center",gap:10,marginBottom:9}}>
      <span style={{width:20,color:t.muted,fontSize:"0.62rem",flexShrink:0}}>#{i+1}</span>
      <span style={{flex:1,fontSize:"0.76rem",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",color:t.text}} title={d.name}>{d.name}</span>
      <div style={{width:`${(d.value/data[0].value)*110}px`,height:6,borderRadius:3,background:CHART_COLORS[i%CHART_COLORS.length],flexShrink:0}}/>
      <span style={{width:32,textAlign:"right",fontSize:"0.68rem",color:t.muted}}>{d.value}</span>
    </div>
  ));
}

// ── Aircraft photo card ────────────────────────────────────────────────────
function AircraftPhotoCard({ flight, theme }) {
  const [photo, setPhoto]   = useState(null);
  const [status, setStatus] = useState("loading");
  const t = theme;

  useEffect(() => {
    if (!flight.registration) { setStatus("error"); return; }
    const reg = flight.registration.replace(/\s+/g,"");
    fetch(`https://api.planespotters.net/pub/photos/reg/${reg}`)
      .then(r=>r.json())
      .then(data => {
        const p = data?.photos?.[0];
        if (p) { setPhoto({thumbnail:p.thumbnail_large?.src||p.thumbnail?.src,link:p.link,photographer:p.photographer}); setStatus("ok"); }
        else setStatus("error");
      })
      .catch(()=>setStatus("error"));
  }, [flight.registration]);

  const psLink = `https://www.planespotters.net/photos/reg/${flight.registration.replace(/\s+/g,"")}`;

  return (
    <div style={{background:t.card,border:`1px solid ${t.border}`,borderRadius:12,overflow:"hidden",display:"flex",flexDirection:"column",boxShadow:t.shadow,transition:"background .25s,border-color .25s"}}>
      <div style={{position:"relative",width:"100%",paddingBottom:"56%",background:t.inputBg,flexShrink:0}}>
        {status==="loading" && <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",color:t.muted,fontSize:"0.72rem",letterSpacing:2}}>LOADING…</div>}
        {status==="ok" && photo?.thumbnail && (
          <a href={photo.link||psLink} target="_blank" rel="noopener noreferrer" style={{position:"absolute",inset:0,display:"block"}}>
            <img src={photo.thumbnail} alt={flight.registration} style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}} onError={()=>setStatus("error")}/>
            <div style={{position:"absolute",bottom:0,left:0,right:0,padding:"6px 10px",background:"rgba(0,0,0,0.55)",fontSize:"0.62rem",color:"rgba(255,255,255,0.7)"}}>
              © {photo.photographer} · via Planespotters.net
            </div>
          </a>
        )}
        {status==="error" && (
          <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:8}}>
            <div style={{fontSize:"2rem",opacity:0.3}}>✈</div>
            <a href={psLink} target="_blank" rel="noopener noreferrer" style={{fontSize:"0.68rem",color:t.accent,textDecoration:"none",letterSpacing:1}}>Search on Planespotters →</a>
          </div>
        )}
      </div>
      <div style={{padding:"14px 16px",flex:1}}>
        <div style={{display:"flex",alignItems:"baseline",gap:8,marginBottom:6}}>
          <span style={{fontFamily:"'Archivo',sans-serif",fontWeight:800,fontSize:"1.1rem",color:t.text}}>{flight.registration||"—"}</span>
          <span style={{fontSize:"0.68rem",color:t.muted}}>{flight.aircraftRaw}</span>
        </div>
        <div style={{fontSize:"0.72rem",color:t.muted,marginBottom:6}}>{flight.airline}</div>
        <div style={{display:"flex",alignItems:"center",gap:6,fontSize:"0.72rem"}}>
          <span style={{background:t.accentBg,color:t.accent,padding:"2px 7px",borderRadius:4}}>{flight.from}</span>
          <span style={{color:t.muted}}>→</span>
          <span style={{background:"rgba(255,107,53,0.12)",color:"#e05a20",padding:"2px 7px",borderRadius:4}}>{flight.to}</span>
          <span style={{marginLeft:"auto",color:t.muted}}>{flight.date}</span>
        </div>
      </div>
    </div>
  );
}

function RecentRegistrations({ flights, theme }) {
  const t = theme;
  const recent = useMemo(() => {
    const seen=new Set(), result=[];
    for (const f of [...flights].sort((a,b)=>b.date.localeCompare(a.date))) {
      if (!f.registration||seen.has(f.registration)) continue;
      seen.add(f.registration); result.push(f);
      if (result.length===10) break;
    }
    return result;
  }, [flights]);
  if (!recent.length) return null;
  return (
    <div style={{marginBottom:32}}>
      <h2 style={{fontFamily:"'Archivo',sans-serif",fontSize:"0.8rem",fontWeight:700,letterSpacing:"2px",textTransform:"uppercase",color:t.muted,marginBottom:16}}>10 Most Recently Flown Registrations</h2>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(400px,1fr))",gap:20}}>
        {recent.map(f=><AircraftPhotoCard key={f.registration} flight={f} theme={t}/>)}
      </div>
      <p style={{marginTop:10,fontSize:"0.65rem",color:t.muted}}>
        Photos from <a href="https://www.planespotters.net" target="_blank" rel="noopener noreferrer" style={{color:t.accent,textDecoration:"none"}}>Planespotters.net</a>. Click any photo for full details.
      </p>
    </div>
  );
}

// ── Most flown registrations ───────────────────────────────────────────────
function MostFlownRegistrations({ flights, theme }) {
  const t = theme;
  const mostFlown = useMemo(() => {
    // Count flights per registration, keep the most recent flight record for each
    const counts = {}, latest = {};
    for (const f of flights) {
      if (!f.registration) continue;
      counts[f.registration] = (counts[f.registration] || 0) + 1;
      if (!latest[f.registration] || f.date > latest[f.registration].date) {
        latest[f.registration] = f;
      }
    }
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([reg, count]) => ({ ...latest[reg], flightCount: count }));
  }, [flights]);

  if (!mostFlown.length) return null;
  return (
    <div style={{marginBottom:32}}>
      <h2 style={{fontFamily:"'Archivo',sans-serif",fontSize:"0.8rem",fontWeight:700,letterSpacing:"2px",textTransform:"uppercase",color:t.muted,marginBottom:16}}>10 Most Flown Registrations</h2>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(400px,1fr))",gap:20}}>
        {mostFlown.map(f=>(
          <div key={f.registration} style={{position:"relative"}}>
            <div style={{position:"absolute",top:12,right:12,zIndex:2,background:t.accent,color:t.bg,fontFamily:"'Archivo',sans-serif",fontWeight:800,fontSize:"0.72rem",padding:"3px 10px",borderRadius:20,letterSpacing:"0.5px"}}>
              {f.flightCount}× flown
            </div>
            <AircraftPhotoCard flight={f} theme={t}/>
          </div>
        ))}
      </div>
      <p style={{marginTop:10,fontSize:"0.65rem",color:t.muted}}>
        Photos from <a href="https://www.planespotters.net" target="_blank" rel="noopener noreferrer" style={{color:t.accent,textDecoration:"none"}}>Planespotters.net</a>. Click any photo for full details.
      </p>
    </div>
  );
}

// ── Flight detail panel ────────────────────────────────────────────────────
function FlightDetailPanel({ flight, onClose, theme }) {
  const t = theme;

  const [psPhoto, setPsPhoto]   = useState(null);
  const [psStatus, setPsStatus] = useState("loading");

  useEffect(() => {
    if (!flight.registration) { setPsStatus("none"); return; }
    const reg = flight.registration.replace(/\s+/g, "");
    fetch(`https://api.planespotters.net/pub/photos/reg/${reg}`)
      .then(r => r.json())
      .then(data => {
        const p = data?.photos?.[0];
        if (p) { setPsPhoto({ src: p.thumbnail_large?.src || p.thumbnail?.src, link: p.link, photographer: p.photographer }); setPsStatus("ok"); }
        else setPsStatus("none");
      })
      .catch(() => setPsStatus("none"));

    const onKey = e => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [flight.registration, onClose]);

  const Row = ({ label, value }) => value ? (
    <div style={{ display:"flex", gap:12, padding:"9px 0", borderBottom:`1px solid ${t.border}` }}>
      <span style={{ width:110, flexShrink:0, fontSize:"0.62rem", letterSpacing:"1.5px", textTransform:"uppercase", color:t.muted, paddingTop:1 }}>{label}</span>
      <span style={{ fontSize:"0.78rem", color:t.text }}>{value}</span>
    </div>
  ) : null;

  const psLink = `https://www.planespotters.net/photos/reg/${(flight.registration||"").replace(/\s+/g,"")}`;

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:200, backdropFilter:"blur(2px)" }}/>

      {/* Panel */}
      <div style={{
        position:"fixed", top:0, right:0, bottom:0, width:"min(520px,100vw)",
        background:t.bg, borderLeft:`1px solid ${t.border}`, zIndex:201,
        overflowY:"auto", boxShadow:"-8px 0 40px rgba(0,0,0,0.4)",
        animation:"slideIn .25s ease",
      }}>
        {/* Header */}
        <div style={{ padding:"20px 24px 16px", borderBottom:`1px solid ${t.border}`, position:"sticky", top:0, background:t.bg, zIndex:1, display:"flex", alignItems:"flex-start", justifyContent:"space-between" }}>
          <div>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:5 }}>
              <span style={{ background:t.accentBg, color:t.accent, padding:"3px 10px", borderRadius:5, fontSize:"0.78rem", fontWeight:700 }}>{flight.from}</span>
              <span style={{ color:t.muted, fontSize:"0.9rem" }}>→</span>
              <span style={{ background:"rgba(255,107,53,0.12)", color:"#e05a20", padding:"3px 10px", borderRadius:5, fontSize:"0.78rem", fontWeight:700 }}>{flight.to}</span>
              {flight.flightNum && <span style={{ color:t.muted, fontSize:"0.72rem" }}>{flight.flightNum}</span>}
            </div>
            <div style={{ fontSize:"0.72rem", color:t.muted }}>
              {flight.date || "Date unknown"}{flight.airline ? ` · ${flight.airline}` : ""}
            </div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:t.muted, fontSize:"1.4rem", cursor:"pointer", lineHeight:1, padding:"0 0 0 16px", flexShrink:0 }}>✕</button>
        </div>

        <div style={{ padding:"20px 24px" }}>

          {/* Planespotters photo */}
          {flight.registration && (
            <div style={{ marginBottom:24 }}>
              <div style={{ fontSize:"0.62rem", letterSpacing:"2px", textTransform:"uppercase", color:t.muted, marginBottom:10 }}>Aircraft Photo</div>
              <div style={{ borderRadius:10, overflow:"hidden", background:t.card, border:`1px solid ${t.border}`, position:"relative", paddingBottom:"56%", width:"100%" }}>
                {psStatus==="loading" && <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", color:t.muted, fontSize:"0.7rem" }}>Loading…</div>}
                {psStatus==="ok" && psPhoto && (
                  <a href={psPhoto.link||psLink} target="_blank" rel="noopener noreferrer" style={{ position:"absolute", inset:0 }}>
                    <img src={psPhoto.src} alt={flight.registration} style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}/>
                    <div style={{ position:"absolute", bottom:0, left:0, right:0, padding:"6px 10px", background:"rgba(0,0,0,0.55)", fontSize:"0.6rem", color:"rgba(255,255,255,0.7)" }}>
                      © {psPhoto.photographer} · Planespotters.net
                    </div>
                  </a>
                )}
                {psStatus==="none" && (
                  <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:6 }}>
                    <div style={{ fontSize:"2rem", opacity:0.2 }}>✈</div>
                    <a href={psLink} target="_blank" rel="noopener noreferrer" style={{ fontSize:"0.66rem", color:t.accent, textDecoration:"none" }}>Search on Planespotters →</a>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Flight details */}
          <div style={{ marginBottom:24 }}>
            <div style={{ fontSize:"0.62rem", letterSpacing:"2px", textTransform:"uppercase", color:t.muted, marginBottom:10 }}>Flight Details</div>
            <div style={{ background:t.card, border:`1px solid ${t.border}`, borderRadius:10, padding:"0 16px" }}>
              <Row label="Date"        value={flight.date}/>
              <Row label="Flight"      value={flight.flightNum}/>
              <Row label="From"        value={`${flight.fromCity} (${flight.from})`}/>
              <Row label="To"          value={`${flight.toCity} (${flight.to})`}/>
              <Row label="Dep"         value={flight.depTime?.slice(0,5)}/>
              <Row label="Arr"         value={flight.arrTime?.slice(0,5)}/>
              <Row label="Duration"    value={flight.durationRaw?.slice(0,5)}/>
              <Row label="Airline"     value={flight.airline}/>
              <Row label="Aircraft"    value={flight.aircraftRaw}/>
              <Row label="Reg"         value={flight.registration}/>
              <Row label="Seat"        value={flight.seat}/>
              <Row label="Seat type"   value={flight.seatType}/>
              <Row label="Class"       value={flight.flightClass}/>
              <Row label="Reason"      value={flight.reason}/>
            </div>
          </div>

          {/* Note / comments */}
          {flight.note && (
            <div style={{ marginBottom:24 }}>
              <div style={{ fontSize:"0.62rem", letterSpacing:"2px", textTransform:"uppercase", color:t.muted, marginBottom:10 }}>Notes</div>
              <div style={{ background:t.card, border:`1px solid ${t.border}`, borderRadius:10, padding:"14px 16px", fontSize:"0.8rem", color:t.text, lineHeight:1.6 }}>
                {flight.note}
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
}

// ── Route map ──────────────────────────────────────────────────────────────
// Airport coordinates (IATA → [lat, lng])
const AIRPORT_COORDS = {
  ABZ:[57.2019,-2.1978],ACC:[5.6052,-0.1668],AKL:[-37.0082,174.7917],AMM:[31.7226,35.9932],
  AMS:[52.3086,4.7639],ARN:[59.6519,17.9186],ATH:[37.9364,23.9445],ATL:[33.6407,-84.4277],
  AUH:[24.4330,54.6511],BCN:[41.2971,2.0785],BEG:[44.8184,20.3091],BFS:[54.6575,-6.2158],
  BGO:[60.2934,5.2181],BHX:[52.4539,-1.7480],BLL:[55.7403,9.1519],BNE:[-27.3842,153.1175],
  BRS:[51.3827,-2.7191],BRU:[50.9010,4.4844],BUD:[47.4298,19.2611],BWI:[39.1754,-76.6682],
  CDG:[49.0097,2.5479],CHC:[-43.4894,172.5322],CMN:[33.3675,-7.5900],CPH:[55.6180,12.6561],
  CPT:[-33.9648,18.6017],DBV:[42.5614,18.2682],DOH:[25.2609,51.6138],DUB:[53.4213,-6.2701],
  DUS:[51.2895,6.7668],DXB:[25.2532,55.3657],EDI:[55.9500,-3.3725],EWR:[40.6895,-74.1745],
  FAO:[37.0144,-7.9659],FCO:[41.7999,12.2462],FRA:[50.0379,8.5622],GLA:[55.8642,-4.4331],
  GOT:[57.6628,12.2798],GVA:[46.2381,6.1089],HAM:[53.6304,9.9882],HEL:[60.3172,24.9633],
  HKG:[22.3080,113.9185],IAD:[38.9531,-77.4565],IST:[41.2753,28.7519],JFK:[40.6413,-73.7781],
  LBA:[53.8659,-1.6606],LCA:[34.8751,33.6249],LGW:[51.1537,-0.1821],LHR:[51.4775,-0.4614],
  LIS:[38.7813,-9.1359],LPA:[27.9319,-15.3866],LTN:[51.8747,-0.3683],MAD:[40.4936,-3.5668],
  MAN:[53.3537,-2.2750],MIA:[25.7959,-80.2870],MLA:[35.8575,14.4775],MMX:[55.5363,13.3762],
  MRS:[43.4393,5.2214],MUC:[48.3538,11.7861],MXP:[45.6306,8.7281],NCL:[55.0375,-1.6917],
  NRT:[35.7653,140.3856],ORD:[41.9742,-87.9073],OSL:[60.1939,11.1004],OTP:[44.5711,26.0850],
  PMI:[39.5517,2.7388],PRG:[50.1008,14.2600],RHO:[36.4054,28.0862],RIX:[56.9236,23.9711],
  SOF:[42.6967,23.4114],STN:[51.8850,0.2350],SVO:[55.9726,37.4146],SYD:[-33.9399,151.1753],
  TFS:[28.0445,-16.5725],TLL:[59.4133,24.8328],TXL:[52.5548,13.2925],VIE:[48.1103,16.5697],
  VNO:[54.6341,25.2858],WAW:[52.1657,20.9671],ZQN:[-45.0211,168.7392],LYS:[45.7256,5.0811],
  BIO:[43.3011,-2.9106],ORK:[51.8413,-8.4910],ABV:[9.0068,7.2632],
  STR:[48.6899,9.2220],LAS:[36.0840,-115.1537],RAK:[31.6069,-8.0363],
  AGP:[36.6749,-4.4991],BKK:[13.6811,100.7472],BOS:[42.3656,-71.0096],
  CWL:[51.3967,-3.3433],EMA:[52.8311,-1.3281],HAJ:[52.4611,9.6850],
  KEF:[63.9850,-22.6056],KTT:[68.0978,27.6978],LAX:[33.9425,-118.4081],
  LCY:[51.5048,-0.0495],LWO:[49.8125,23.9561],NCE:[43.6584,7.2159],
  OPO:[41.2481,-8.6814],SFO:[37.6213,-122.3790],SOU:[50.9503,-1.3568],
  YVR:[49.1967,-123.1815],ZRH:[47.4647,8.5492],
};

// Great-circle interpolation
function gcPoints(lat1, lng1, lat2, lng2, n=80) {
  const toR = d => d * Math.PI / 180;
  const toD = r => r * 180 / Math.PI;
  const [φ1,λ1,φ2,λ2] = [toR(lat1),toR(lng1),toR(lat2),toR(lng2)];
  const pts = [];
  for (let i = 0; i <= n; i++) {
    const f = i / n;
    const A = Math.sin((1-f)*Math.PI) / Math.sin(Math.PI);
    const B = Math.sin(f*Math.PI) / Math.sin(Math.PI);
    // Use simple interpolation along great circle
    const x = A*Math.cos(φ1)*Math.cos(λ1) + B*Math.cos(φ2)*Math.cos(λ2);
    const y = A*Math.cos(φ1)*Math.sin(λ1) + B*Math.cos(φ2)*Math.sin(λ2);
    const z = A*Math.sin(φ1)              + B*Math.sin(φ2);
    pts.push([toD(Math.atan2(z, Math.sqrt(x*x+y*y))), toD(Math.atan2(y,x))]);
  }
  return pts;
}

function RouteMap({ flights, theme, themeMode }) {
  const mapRef   = useRef(null);
  const leafRef  = useRef(null);
  const t = theme;

  // Compute routes with counts
  const { routes, airports } = useMemo(() => {
    const counts = {};
    const apSet  = new Set();
    flights.forEach(f => {
      if (!f.from || !f.to) return;
      apSet.add(f.from); apSet.add(f.to);
      const key = [f.from, f.to].sort().join('|');
      counts[key] = (counts[key]||0) + 1;
    });
    const maxCount = Math.max(...Object.values(counts), 1);
    const routes = Object.entries(counts).map(([key, count]) => {
      const [a, b] = key.split('|');
      return { from: a, to: b, count, ratio: count / maxCount };
    });
    return { routes, airports: [...apSet] };
  }, [flights]);

  // Resolved theme for tiles
  const isDark = useMemo(() => {
    if (themeMode === 'auto') return window.matchMedia('(prefers-color-scheme: dark)').matches;
    return themeMode === 'dark';
  }, [themeMode]);

  useEffect(() => {
    if (!mapRef.current || leafRef.current) return;

    // Load Leaflet dynamically
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css';
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js';
    script.onload = () => initMap();
    document.head.appendChild(script);

    function initMap() {
      const L = window.L;
      const map = L.map(mapRef.current, {
        center: [30, 10], zoom: 2,
        zoomControl: true,
        attributionControl: true,
      });
      leafRef.current = map;

      const darkTile  = 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';
      const lightTile = 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';
      const attr = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>';

      L.tileLayer(isDark ? darkTile : lightTile, { attribution: attr, maxZoom: 19 }).addTo(map);

      // Draw routes as polylines
      routes.forEach(({ from, to, count, ratio }) => {
        const c1 = AIRPORT_COORDS[from], c2 = AIRPORT_COORDS[to];
        if (!c1 || !c2) return;

        const pts = gcPoints(c1[0], c1[1], c2[0], c2[1]);

        // Colour: low = dim blue, high = bright cyan
        const r  = Math.round(0   + ratio * 0);
        const g  = Math.round(100 + ratio * 112);
        const b  = Math.round(180 + ratio * 75);
        const a  = 0.25 + ratio * 0.65;
        const w  = 1.5 + ratio * 7;

        const line = L.polyline(pts, {
          color: `rgba(${r},${g},${b},${a})`,
          weight: w,
          smoothFactor: 1,
        }).addTo(map);
        line.bindTooltip(`${from} → ${to}: ${count} flight${count>1?'s':''}`, { sticky: true });
      });

      // Airport dots
      airports.forEach(iata => {
        const c = AIRPORT_COORDS[iata];
        if (!c) return;
        L.circleMarker(c, {
          radius: 3,
          fillColor: isDark ? '#00d4ff' : '#0080b3',
          color: isDark ? '#050d1a' : '#ffffff',
          weight: 1.5,
          fillOpacity: 0.9,
        }).addTo(map).bindTooltip(iata, { permanent: false });
      });
    }

    return () => { /* cleanup handled by leafRef check */ };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Re-draw when theme changes
  useEffect(() => {
    if (!leafRef.current) return;
    const L = window.L;
    if (!L) return;
    const map = leafRef.current;
    map.eachLayer(l => { if (l._url) map.removeLayer(l); });
    const darkTile  = 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';
    const lightTile = 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';
    const attr = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>';
    L.tileLayer(isDark ? darkTile : lightTile, { attribution: attr, maxZoom: 19 }).addTo(map);
  }, [isDark]);

  // Missing airport warning
  const missing = useMemo(() => {
    const s = new Set();
    routes.forEach(({from,to}) => {
      if (!AIRPORT_COORDS[from]) s.add(from);
      if (!AIRPORT_COORDS[to])   s.add(to);
    });
    return [...s];
  }, [routes]);

  return (
    <div>
      {/* Legend */}
      <div style={{display:'flex',alignItems:'center',gap:24,marginBottom:16,flexWrap:'wrap'}}>
        <div style={{fontSize:'0.62rem',letterSpacing:'2px',textTransform:'uppercase',color:t.muted}}>
          Route frequency
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <div style={{width:40,height:3,background:`rgba(0,100,180,0.4)`,borderRadius:2}}/>
          <span style={{fontSize:'0.68rem',color:t.muted}}>Rare</span>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <div style={{width:40,height:6,background:`rgba(0,180,240,0.75)`,borderRadius:2}}/>
          <span style={{fontSize:'0.68rem',color:t.muted}}>Frequent</span>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <div style={{width:40,height:10,background:`rgba(0,212,255,0.9)`,borderRadius:2}}/>
          <span style={{fontSize:'0.68rem',color:t.muted}}>Most flown</span>
        </div>
        <div style={{marginLeft:'auto',fontSize:'0.68rem',color:t.muted}}>
          {routes.length} routes · {airports.length} airports
        </div>
      </div>

      {/* Map container */}
      <div
        ref={mapRef}
        style={{
          width:'100%', height:'70vh', borderRadius:12,
          border:`1px solid ${t.border}`,
          overflow:'hidden', background: isDark ? '#0a1628' : '#e8f0f8',
        }}
      />

      {missing.length > 0 && (
        <p style={{marginTop:10,fontSize:'0.65rem',color:t.muted}}>
          ⚠ Coordinates not found for: {missing.join(', ')} — these routes are hidden.
        </p>
      )}
      <p style={{marginTop:6,fontSize:'0.65rem',color:t.muted}}>
        Hover a route for details · Scroll to zoom · Drag to pan
      </p>
    </div>
  );
}

// ── Theme toggle widget ────────────────────────────────────────────────────
function ThemeToggle({ mode, onToggle }) {
  return (
    <div className="theme-toggle" title="Toggle light/dark mode">
      <button className={mode==="dark"  ? "active" : ""} onClick={e=>{e.stopPropagation();onToggle("dark");}}>🌙</button>
      <button className={mode==="auto"  ? "active" : ""} onClick={e=>{e.stopPropagation();onToggle("auto");}}>⚙</button>
      <button className={mode==="light" ? "active" : ""} onClick={e=>{e.stopPropagation();onToggle("light");}}>☀️</button>
    </div>
  );
}

// ── Admin panel ────────────────────────────────────────────────────────────
function AdminPanel({ onFlightsUploaded, flightCount, onClearData, theme, onLogin }) {
  const [pw, setPw]         = useState("");
  const [authed, setAuthed] = useState(false);
  const [drag, setDrag]     = useState(false);
  const [status, setStatus] = useState(null);
  const [msg, setMsg]       = useState("");
  const [pwErr, setPwErr]   = useState("");
  const t = theme;

  const tryLogin = () => {
    if (pw === ADMIN_PASSWORD) { setAuthed(true); onLogin && onLogin(); }
    else { setPwErr("Incorrect password"); setPw(""); }
  };

  const handleFile = file => {
    if (!file) return;
    setStatus("uploading"); setMsg("");
    parseFlightDiaryCSV(file, async flights => {
      if (!flights.length) { setStatus("error"); setMsg("No flights found — check the file format."); return; }
      try { await onFlightsUploaded(flights); setStatus("done"); setMsg(`✓ ${flights.length} flights saved. All devices will see updated data.`); }
      catch(e) { setStatus("error"); setMsg(`Save failed: ${e.message}`); }
    });
  };

  if (!authed) return (
    <div style={{maxWidth:380,margin:"60px auto",background:t.card,border:`1px solid ${t.border}`,borderRadius:12,padding:36,textAlign:"center",boxShadow:t.shadow}}>
      <div style={{fontSize:"2rem",marginBottom:16}}>🔒</div>
      <h2 style={{fontFamily:"'Archivo',sans-serif",fontWeight:800,marginBottom:8,color:t.text}}>Admin Access</h2>
      <p style={{color:t.muted,fontSize:"0.76rem",marginBottom:24}}>Enter the admin password to manage flight data</p>
      <input type="password" value={pw} placeholder="Password"
        onChange={e=>{setPw(e.target.value);setPwErr("");}}
        onKeyDown={e=>e.key==="Enter"&&tryLogin()}
        style={{width:"100%",background:t.inputBg,border:`1px solid ${pwErr?'#e05a20':t.border}`,borderRadius:6,padding:"10px 14px",color:t.text,fontFamily:"'DM Mono',monospace",fontSize:"0.8rem",outline:"none",marginBottom:8,textAlign:"center"}}
        autoFocus/>
      {pwErr && <div style={{color:"#e05a20",fontSize:"0.7rem",marginBottom:8}}>{pwErr}</div>}
      <button onClick={tryLogin} style={{width:"100%",padding:"11px",background:t.accent,color:t.bg,border:"none",borderRadius:6,fontFamily:"'DM Mono',monospace",fontSize:"0.76rem",letterSpacing:"1px",fontWeight:600,cursor:"pointer",marginTop:4}}>UNLOCK</button>
    </div>
  );

  return (
    <div style={{maxWidth:600,margin:"0 auto"}}>
      <div style={{background:t.card,border:`1px solid ${t.border}`,borderRadius:12,padding:28,marginBottom:20,boxShadow:t.shadow}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
          <div>
            <h2 style={{fontFamily:"'Archivo',sans-serif",fontWeight:800,color:t.text,marginBottom:4}}>Upload Flight Data</h2>
            <p style={{color:t.muted,fontSize:"0.72rem"}}>Currently stored: <span style={{color:t.accent}}>{flightCount} flights</span></p>
          </div>
          <div style={{background:t.accentBg,border:`1px solid ${t.accent}33`,borderRadius:6,padding:"4px 12px",fontSize:"0.65rem",color:t.accent,letterSpacing:2}}>ADMIN</div>
        </div>
        <div
          style={{border:`1.5px dashed ${drag?t.accent:t.border}`,borderRadius:12,padding:"40px 24px",textAlign:"center",cursor:"pointer",background:drag?t.accentBg:t.inputBg,transition:"all .3s",marginBottom:16}}
          onDragOver={e=>{e.preventDefault();setDrag(true);}}
          onDragLeave={()=>setDrag(false)}
          onDrop={e=>{e.preventDefault();setDrag(false);handleFile(e.dataTransfer.files[0]);}}
          onClick={()=>document.getElementById("admin-fi").click()}
        >
          <div style={{fontSize:"2rem",marginBottom:10,opacity:0.6}}>📂</div>
          <p style={{color:t.text,fontSize:"0.82rem",fontWeight:500,marginBottom:6}}>Drop flightdiary.net CSV here</p>
          <p style={{color:t.muted,fontSize:"0.72rem"}}>or click to browse</p>
          <input id="admin-fi" type="file" accept=".csv" style={{display:"none"}} onChange={e=>{if(e.target.files[0]) handleFile(e.target.files[0]);}}/>
        </div>
        {status==="uploading" && <div style={{textAlign:"center",color:t.muted,fontSize:"0.76rem",padding:"12px 0"}}>Parsing and saving to database…</div>}
        {status==="done"      && <div style={{textAlign:"center",color:"#00a050",fontSize:"0.76rem",padding:"12px 0"}}>{msg}</div>}
        {status==="error"     && <div style={{textAlign:"center",color:"#e05a20",fontSize:"0.76rem",padding:"12px 0"}}>{msg}</div>}
      </div>
      {flightCount > 0 && (
        <div style={{background:t.card,border:"1px solid rgba(220,80,50,0.25)",borderRadius:12,padding:24,boxShadow:t.shadow}}>
          <h3 style={{fontFamily:"'Archivo',sans-serif",fontSize:"0.78rem",fontWeight:700,letterSpacing:"1.5px",textTransform:"uppercase",color:"#e05a20",marginBottom:8}}>Danger Zone</h3>
          <p style={{color:t.muted,fontSize:"0.72rem",marginBottom:16}}>Permanently remove all stored flight data. This cannot be undone.</p>
          <button onClick={onClearData} style={{padding:"9px 20px",background:"none",border:"1px solid rgba(220,80,50,0.4)",color:"#e05a20",borderRadius:6,fontFamily:"'DM Mono',monospace",fontSize:"0.72rem",cursor:"pointer"}}>Clear All Flight Data</button>
        </div>
      )}
    </div>
  );
}


// ── Main app ───────────────────────────────────────────────────────────────
export default function FlightTracker() {
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");
  const [tab, setTab]         = useState("overview");
  const [search, setSearch]   = useState("");
  const [fAirline, setFA]     = useState("");
  const [fClass, setFC]       = useState("");
  const [fReason, setFR]      = useState("");
  const [sortCol, setSC]      = useState("date");
  const [sortAsc, setSA]        = useState(false);
  const [selectedFlight, setSelectedFlight] = useState(null);

  // ── Theme ────────────────────────────────────────────────────────────
  // mode: "dark" | "light" | "auto"
  const [themeMode, setThemeMode] = useState(() => {
    return localStorage.getItem("flightlog-theme") || "auto";
  });

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => { if (themeMode === "auto") setThemeMode("auto"); };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [themeMode]);

  const resolvedTheme = useMemo(() => {
    if (themeMode === "auto") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }
    return themeMode;
  }, [themeMode]);

  const theme = THEMES[resolvedTheme];

  const handleThemeToggle = (mode) => {
    setThemeMode(mode);
    localStorage.setItem("flightlog-theme", mode);
  };

  // ── Load from Supabase ───────────────────────────────────────────────
  useEffect(() => {
    dbLoad()
      .then(data => setFlights(Array.isArray(data) ? data : []))
      .catch(e => setLoadErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  const saveFlights = async newFlights => { await dbSave(newFlights); setFlights(newFlights); };
  const clearFlights = async () => {
    if (!window.confirm("Delete all stored flight data? This cannot be undone.")) return;
    await dbSave([]); setFlights([]);
  };

  // ── Stats ────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    if (!flights.length) return null;
    const totalMins = flights.reduce((s,f)=>s+(f.durationMins||0),0);
    const routes = flights.map(f=>f.from&&f.to?`${f.from} → ${f.to}`:"").filter(Boolean);
    const airports = [...flights.map(f=>f.from),...flights.map(f=>f.to)].filter(Boolean);
    const byMonth={}, byYear={};
    flights.forEach(f=>{
      if (!f.date) return;
      const d=new Date(f.date); if(isNaN(d)) return;
      const ym=`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
      byMonth[ym]=(byMonth[ym]||0)+1;
      byYear[d.getFullYear()]=(byYear[d.getFullYear()]||0)+1;
    });
    return {
      total:flights.length, totalMins,
      uAirlines:        new Set(flights.map(f=>f.airline).filter(Boolean)).size,
      uAirports:        new Set(airports).size,
      uAircraftFamilies:new Set(flights.map(f=>f.aircraftFamily).filter(Boolean)).size,
      uRegs:            new Set(flights.map(f=>f.registration).filter(Boolean)).size,
      topAirlines:         top(flights.map(f=>f.airline), 10),
      topAircraftFamilies: top(flights.map(f=>f.aircraftFamily), 10),
      topRoutes:           top(routes),
      topAirports:         top(airports,10),
      topClasses:          top(flights.map(f=>f.flightClass)),
      topReasons:          top(flights.map(f=>f.reason)),
      topSeatTypes:        top(flights.map(f=>f.seatType),4),
      byMonth: Object.entries(byMonth).sort().map(([name,value])=>({name,value})),
      byYear:  Object.entries(byYear).sort().map(([name,value])=>({name:String(name),value})),
      allAirlines:[...new Set(flights.map(f=>f.airline).filter(Boolean))].sort(),
      allClasses: [...new Set(flights.map(f=>f.flightClass).filter(Boolean))].sort(),
      allReasons: [...new Set(flights.map(f=>f.reason).filter(Boolean))].sort(),
    };
  }, [flights]);

  const filtered = useMemo(() => {
    let rows=[...flights];
    if (fAirline) rows=rows.filter(f=>f.airline===fAirline);
    if (fClass)   rows=rows.filter(f=>f.flightClass===fClass);
    if (fReason)  rows=rows.filter(f=>f.reason===fReason);
    if (search) { const s=search.toLowerCase(); rows=rows.filter(f=>Object.values(f).some(v=>String(v).toLowerCase().includes(s))); }
    rows.sort((a,b)=>{ const av=String(a[sortCol]||""),bv=String(b[sortCol]||""); return sortAsc?av.localeCompare(bv):bv.localeCompare(av); });
    return rows;
  }, [flights,search,fAirline,fClass,fReason,sortCol,sortAsc]);

  const sort = col => { if(sortCol===col) setSA(v=>!v); else{setSC(col);setSA(true);} };
  const SH = ({col,label}) => <th onClick={()=>sort(col)}>{label}{sortCol===col?(sortAsc?" ↑":" ↓"):""}</th>;
  const t = theme;
  const makeTip = (props) => <Tip {...props} theme={t}/>;

  const NAVS = [
    {id:"overview",  l:"📊 Summary"},
    {id:"recent",    l:"✈ Recent Aircraft"},
    {id:"mostflown", l:"🏆 Most Flown"},
    {id:"map",       l:"🗺 Route Map"},
    {id:"charts",    l:"📈 Charts"},
    {id:"search",    l:"🔍 Search"},
    {id:"admin",     l:"⚙ Admin"},
  ];

  const noDataMsg = <div className="empty"><h3>No data yet</h3><p>An admin needs to upload flight data first.</p></div>;

  if (loading) return (
    <>
      <style>{makeCSS(t)}</style>
      <div className="app">
        <div className="hdr"><div className="hdr-top"><div><h1>GOGOW <span>FLIGHTS</span></h1></div></div></div>
        <div className="loading"><div className="spin">✈</div><br/>Loading flight data…</div>
      </div>
    </>
  );

  if (loadErr) return (
    <>
      <style>{makeCSS(t)}</style>
      <div className="app">
        <div className="hdr"><h1>GOGOW <span>FLIGHTS</span></h1></div>
        <div className="loading" style={{color:"#e05a20"}}>
          <div style={{fontSize:"2rem",marginBottom:12}}>⚠</div>
          Could not connect to database.<br/>
          <span style={{fontSize:"0.7rem",color:t.muted,marginTop:8,display:"block"}}>{loadErr}</span>
        </div>
      </div>
    </>
  );

  return (
    <>
      <style>{makeCSS(t)}</style>
      <div className="app">
        <div className="hdr">
          <div className="hdr-top">
            <div>
              <h1>GOGOW <span>FLIGHTS</span></h1>
              
            </div>
            <ThemeToggle mode={themeMode} onToggle={handleThemeToggle}/>
          </div>
          <div className="nav">
            {NAVS.map(n=><button key={n.id} className={tab===n.id?"on":""} onClick={()=>setTab(n.id)}>{n.l}</button>)}
          </div>
        </div>

        <div className="body">

          {tab==="admin" && <AdminPanel onFlightsUploaded={saveFlights} flightCount={flights.length} onClearData={clearFlights} theme={t} onLogin={null}/>}

          {tab==="overview" && (!stats
            ? <div className="empty"><h3>No flight data yet</h3><p style={{marginBottom:16}}>An admin needs to upload a flight log first.</p><button onClick={()=>setTab("admin")} style={{padding:"9px 20px",background:t.accent,color:t.bg,border:"none",borderRadius:6,fontFamily:"'DM Mono',monospace",fontSize:"0.74rem",cursor:"pointer"}}>Go to Admin →</button></div>
            : <>
                <div className="sgrid">
                  <div className="sc"><div className="lb">Total Flights</div><div className="vl">{stats.total}</div></div>
                  <div className="sc"><div className="lb">Total Airtime</div><div className="vl" style={{fontSize:"1.3rem"}}>{fmtDur(stats.totalMins)}</div></div>
                  <div className="sc"><div className="lb">Airlines</div><div className="vl">{stats.uAirlines}</div><div className="sb2">unique carriers</div></div>
                  <div className="sc"><div className="lb">Airports</div><div className="vl">{stats.uAirports}</div><div className="sb2">visited</div></div>
                  <div className="sc"><div className="lb">Aircraft Families</div><div className="vl">{stats.uAircraftFamilies}</div><div className="sb2">distinct types</div></div>
                  <div className="sc"><div className="lb">Tail Numbers</div><div className="vl">{stats.uRegs}</div><div className="sb2">registrations</div></div>
                  {stats.topAirlines[0]        && <div className="sc"><div className="lb">Top Airline</div><div className="vl" style={{fontSize:"1rem",paddingTop:4}}>{stats.topAirlines[0].name}</div><div className="sb2">{stats.topAirlines[0].value} flights</div></div>}
                  {stats.topRoutes[0]           && <div className="sc"><div className="lb">Busiest Route</div><div className="vl" style={{fontSize:"0.82rem",paddingTop:4}}>{stats.topRoutes[0].name}</div><div className="sb2">{stats.topRoutes[0].value}×</div></div>}
                  {stats.topAircraftFamilies[0] && <div className="sc"><div className="lb">Top Aircraft</div><div className="vl" style={{fontSize:"0.82rem",paddingTop:4}}>{stats.topAircraftFamilies[0].name}</div><div className="sb2">{stats.topAircraftFamilies[0].value} flights</div></div>}
                </div>
                <div className="g2">
                  {stats.topAirlines.length>0        && <div className="cc"><h3>Top Airlines</h3><BarList data={stats.topAirlines} theme={t}/></div>}
                  {stats.topRoutes.length>0           && <div className="cc"><h3>Top Routes</h3><BarList data={stats.topRoutes} theme={t}/></div>}
                  {stats.topAirports.length>0         && <div className="cc"><h3>Most Visited Airports</h3><BarList data={stats.topAirports} theme={t}/></div>}
                  {stats.topAircraftFamilies.length>0 && <div className="cc"><h3>Aircraft Families</h3><BarList data={stats.topAircraftFamilies} theme={t}/></div>}
                  {stats.topReasons.length>0          && <div className="cc"><h3>Flight Reasons</h3><BarList data={stats.topReasons} theme={t}/></div>}
                  {stats.topClasses.length>0          && <div className="cc"><h3>Cabin Class</h3><BarList data={stats.topClasses} theme={t}/></div>}
                  {stats.topSeatTypes.length>0        && <div className="cc"><h3>Seat Type</h3><BarList data={stats.topSeatTypes} theme={t}/></div>}
                </div>
              </>
          )}

          {tab==="recent"    && (!flights.length ? noDataMsg : <RecentRegistrations flights={flights} theme={t}/>)}
          {tab==="mostflown" && (!flights.length ? noDataMsg : <MostFlownRegistrations flights={flights} theme={t}/>)}
          {tab==="map"       && (!flights.length ? noDataMsg : <RouteMap flights={flights} theme={t} themeMode={themeMode}/>)}

          {tab==="charts" && (!stats ? noDataMsg :
            <>
              <div className="g2">
                {stats.topAirlines.length>0 && (
                  <div className="cc"><h3>Airlines</h3>
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={stats.topAirlines} margin={{top:5,right:10,left:-20,bottom:50}}>
                        <CartesianGrid strokeDasharray="3 3" stroke={t.border}/>
                        <XAxis dataKey="name" tick={{fill:t.muted,fontSize:9}} angle={-35} textAnchor="end"/>
                        <YAxis tick={{fill:t.muted,fontSize:10}}/>
                        <Tooltip content={makeTip}/>
                        <Bar dataKey="value" radius={[4,4,0,0]}>{stats.topAirlines.map((_,i)=><Cell key={i} fill={CHART_COLORS[i%CHART_COLORS.length]}/>)}</Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
                {stats.topAircraftFamilies.length>0 && (
                  <div className="cc"><h3>Aircraft Families</h3>
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie data={stats.topAircraftFamilies} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={95} paddingAngle={2} label={({name,percent})=>`${name} ${(percent*100).toFixed(0)}%`} labelLine={{stroke:t.muted}} fontSize={9} fill={t.text}>
                          {stats.topAircraftFamilies.map((_,i)=><Cell key={i} fill={CHART_COLORS[i%CHART_COLORS.length]}/>)}
                        </Pie>
                        <Tooltip content={makeTip}/>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
                {stats.topClasses.length>0 && (
                  <div className="cc"><h3>Cabin Class</h3>
                    <ResponsiveContainer width="100%" height={240}>
                      <PieChart>
                        <Pie data={stats.topClasses} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} paddingAngle={3} label={({name,percent})=>`${name} ${(percent*100).toFixed(0)}%`} labelLine={{stroke:t.muted}} fontSize={10}>
                          {stats.topClasses.map((_,i)=><Cell key={i} fill={CHART_COLORS[i%CHART_COLORS.length]}/>)}
                        </Pie>
                        <Tooltip content={makeTip}/>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
                {stats.topReasons.length>0 && (
                  <div className="cc"><h3>Flight Reasons</h3>
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={stats.topReasons} layout="vertical" margin={{top:5,right:20,left:20,bottom:5}}>
                        <CartesianGrid strokeDasharray="3 3" stroke={t.border}/>
                        <XAxis type="number" tick={{fill:t.muted,fontSize:10}}/>
                        <YAxis dataKey="name" type="category" tick={{fill:t.muted,fontSize:11}} width={70}/>
                        <Tooltip content={makeTip}/>
                        <Bar dataKey="value" radius={[0,4,4,0]}>{stats.topReasons.map((_,i)=><Cell key={i} fill={CHART_COLORS[i%CHART_COLORS.length]}/>)}</Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
                {stats.byYear.length>0 && (
                  <div className="cc full"><h3>Flights Per Year</h3>
                    <ResponsiveContainer width="100%" height={190}>
                      <BarChart data={stats.byYear} margin={{top:5,right:20,left:-10,bottom:5}}>
                        <CartesianGrid strokeDasharray="3 3" stroke={t.border}/>
                        <XAxis dataKey="name" tick={{fill:t.muted,fontSize:10}}/>
                        <YAxis tick={{fill:t.muted,fontSize:10}}/>
                        <Tooltip content={makeTip}/>
                        <Bar dataKey="value" fill={t.accent} radius={[4,4,0,0]}/>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
                {stats.topAirports.length>0 && (
                  <div className="cc full"><h3>Most Visited Airports</h3>
                    <ResponsiveContainer width="100%" height={210}>
                      <BarChart data={stats.topAirports} margin={{top:5,right:20,left:-10,bottom:5}}>
                        <CartesianGrid strokeDasharray="3 3" stroke={t.border}/>
                        <XAxis dataKey="name" tick={{fill:t.muted,fontSize:10}}/>
                        <YAxis tick={{fill:t.muted,fontSize:10}}/>
                        <Tooltip content={makeTip}/>
                        <Bar dataKey="value" radius={[4,4,0,0]}>{stats.topAirports.map((_,i)=><Cell key={i} fill={CHART_COLORS[i%CHART_COLORS.length]}/>)}</Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </>
          )}

          {tab==="search" && (!flights.length ? noDataMsg :
            <>
              <div className="sb">
                <input placeholder="Search flights, airports, aircraft…" value={search} onChange={e=>setSearch(e.target.value)}/>
                {stats?.allAirlines.length>0 && <select value={fAirline} onChange={e=>setFA(e.target.value)}><option value="">All Airlines</option>{stats.allAirlines.map(a=><option key={a} value={a}>{a}</option>)}</select>}
                {stats?.allClasses.length>0  && <select value={fClass}   onChange={e=>setFC(e.target.value)}><option value="">All Classes</option>{stats.allClasses.map(c=><option key={c} value={c}>{c}</option>)}</select>}
                {stats?.allReasons.length>0  && <select value={fReason}  onChange={e=>setFR(e.target.value)}><option value="">All Reasons</option>{stats.allReasons.map(r=><option key={r} value={r}>{r}</option>)}</select>}
                {(search||fAirline||fClass||fReason) && <button onClick={()=>{setSearch("");setFA("");setFC("");setFR("");}} style={{padding:"9px 16px",background:"none",border:`1px solid ${t.border}`,color:t.text,borderRadius:6,fontFamily:"'DM Mono',monospace",fontSize:"0.74rem",cursor:"pointer"}}>Clear</button>}
              </div>
              <div className="rc">{filtered.length} of {flights.length} flights</div>
              <div className="tw">
                <table>
                  <thead><tr>
                    <SH col="date" label="Date"/><SH col="flightNum" label="Flight"/>
                    <SH col="from" label="From"/><SH col="to" label="To"/>
                    <SH col="airline" label="Airline"/><SH col="aircraftRaw" label="Aircraft"/>
                    <SH col="aircraftFamily" label="Family"/><SH col="registration" label="Reg"/>
                    <SH col="depTime" label="Dep"/><SH col="arrTime" label="Arr"/>
                    <SH col="durationRaw" label="Duration"/><SH col="seat" label="Seat"/>
                    <SH col="seatType" label="Type"/><SH col="flightClass" label="Class"/>
                    <SH col="reason" label="Reason"/>
                  </tr></thead>
                  <tbody>
                    {filtered.slice(0,300).map((f,i)=>(
                      <tr key={i} className="clickable" onClick={()=>setSelectedFlight(f)}>
                        <td>{f.date}</td>
                        <td style={{color:t.muted}}>{f.flightNum}</td>
                        <td><span className="t tb" title={f.fromCity}>{f.from}</span></td>
                        <td><span className="t to2" title={f.toCity}>{f.to}</span></td>
                        <td>{f.airline}</td>
                        <td style={{color:t.muted,fontSize:"0.7rem"}}>{f.aircraftRaw}</td>
                        <td><span className="t ty">{f.aircraftFamily}</span></td>
                        <td style={{color:t.muted,fontSize:"0.68rem"}}>{f.registration}</td>
                        <td style={{color:t.muted}}>{f.depTime}</td>
                        <td style={{color:t.muted}}>{f.arrTime}</td>
                        <td>{f.durationRaw}</td>
                        <td style={{color:t.muted}}>{f.seat}</td>
                        <td>{f.seatType&&<span className="t tg">{f.seatType}</span>}</td>
                        <td>{f.flightClass&&<span className="t tp">{f.flightClass}</span>}</td>
                        <td>{f.reason&&<span className="t ty">{f.reason}</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filtered.length===0 && <div className="empty"><p>No flights match</p></div>}
                {filtered.length>300 && <div style={{padding:"11px 16px",color:t.muted,fontSize:"0.68rem"}}>Showing first 300 of {filtered.length} results</div>}
              </div>
            </>
          )}

        </div>
      </div>

      {selectedFlight && (
        <FlightDetailPanel
          flight={selectedFlight}
          onClose={() => setSelectedFlight(null)}
          theme={t}
        />
      )}
    </>
  );
}
